import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {CloudDownload, PlayArrow, Refresh} from '@mui/icons-material';
import {
    useRegenerateTtsVoiceSample,
    useSyncTtsVoices,
    useTtsVoiceList,
    useUpdateTtsSampleText,
    useUpdateTtsVoice,
} from '../hooks/useTtsVoice.ts';
import {AdminVoiceItem, VoiceSyncResult} from '../types/ttsVoice.ts';

const LANGS = ['en', 'ko', 'ja', 'zh', 'vi', 'es', 'de', 'fr', 'it', 'pt', 'id'];

interface VoiceRowProps {
    row: AdminVoiceItem;
    onPlay: (id: number, url: string) => void;
    playingId: number | null;
}

const VoiceRow: FC<VoiceRowProps> = ({row, onPlay, playingId}) => {
    // 서버 값이 바뀌면 parent가 key로 remount 시켜 초기값을 재동기화
    const [displayKo, setDisplayKo] = useState(row.displayNameI18n?.ko ?? '');
    const [displayEn, setDisplayEn] = useState(row.displayNameI18n?.en ?? '');

    const updateMut = useUpdateTtsVoice();
    const regenerateMut = useRegenerateTtsVoiceSample();

    const commitDisplayName = () => {
        const prevKo = row.displayNameI18n?.ko ?? '';
        const prevEn = row.displayNameI18n?.en ?? '';
        if (displayKo === prevKo && displayEn === prevEn) return;
        const nextMap: Record<string, string> = {
            ...(row.displayNameI18n ?? {}),
            ko: displayKo,
            en: displayEn,
        };
        updateMut.mutate({id: row.id, body: {displayNameI18n: nextMap}});
    };

    const toggleDefault = (checked: boolean) => {
        if (!checked) return; // default는 켜기만 가능, 끄려면 다른 row에서 켜야 함
        updateMut.mutate({id: row.id, body: {isDefault: true}});
    };

    const toggleActive = (checked: boolean) => {
        updateMut.mutate({id: row.id, body: {isActive: checked}});
    };

    const handleRegenerate = () => {
        if (!confirm(`"${row.voiceName}" 샘플을 재생성하시겠습니까?`)) return;
        regenerateMut.mutate({id: row.id});
    };

    const isPlaying = playingId === row.id;

    return (
        <TableRow hover>
            <TableCell>
                <Stack direction="column" spacing={0.5}>
                    <Typography variant="body2" sx={{fontFamily: 'monospace'}}>{row.voiceName}</Typography>
                    {row.sampleAudioText && (
                        <Typography variant="caption" color="text.secondary">
                            {row.sampleAudioText.length > 40
                                ? row.sampleAudioText.slice(0, 40) + '...'
                                : row.sampleAudioText}
                        </Typography>
                    )}
                </Stack>
            </TableCell>
            <TableCell>
                <Chip label={row.voiceType} size="small" variant="outlined"/>
            </TableCell>
            <TableCell>
                <Chip
                    label={row.gender}
                    size="small"
                    color={row.gender === 'FEMALE' ? 'secondary' : row.gender === 'MALE' ? 'primary' : 'default'}
                    variant="outlined"
                />
            </TableCell>
            <TableCell>
                <TextField
                    size="small"
                    value={displayKo}
                    onChange={e => setDisplayKo(e.target.value)}
                    onBlur={commitDisplayName}
                    sx={{minWidth: 160}}
                />
            </TableCell>
            <TableCell>
                <TextField
                    size="small"
                    value={displayEn}
                    onChange={e => setDisplayEn(e.target.value)}
                    onBlur={commitDisplayName}
                    sx={{minWidth: 160}}
                />
            </TableCell>
            <TableCell align="center">
                <Switch
                    size="small"
                    checked={row.isDefault}
                    onChange={e => toggleDefault(e.target.checked)}
                    disabled={updateMut.isPending || row.isDefault}
                />
            </TableCell>
            <TableCell align="center">
                <Switch
                    size="small"
                    checked={row.isActive}
                    onChange={e => toggleActive(e.target.checked)}
                    disabled={updateMut.isPending}
                />
            </TableCell>
            <TableCell align="center">
                <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={row.sampleAudioUrl ? '샘플 재생' : '샘플 없음'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => row.sampleAudioUrl && onPlay(row.id, row.sampleAudioUrl)}
                                disabled={!row.sampleAudioUrl}
                                color={isPlaying ? 'primary' : 'default'}
                            >
                                <PlayArrow fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="샘플 재생성">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleRegenerate}
                                disabled={regenerateMut.isPending}
                            >
                                <Refresh fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

interface SampleTextEditorProps {
    lang: string;
    initialText: string;
}

/**
 * 분리된 편집기 — 언어/서버 값이 바뀌면 부모가 key로 remount 시켜 초기값을 재동기화.
 */
const SampleTextEditor: FC<SampleTextEditorProps> = ({lang, initialText}) => {
    const [sampleText, setSampleText] = useState<string>(initialText);
    const updateSampleTextMut = useUpdateTtsSampleText();

    const handleSaveSampleText = () => {
        if (!sampleText.trim()) return;
        updateSampleTextMut.mutate({code: lang, sampleText: sampleText.trim()});
    };

    return (
        <>
            <TextField
                size="small"
                label="샘플 텍스트 (language_specs.tts_sample_text)"
                value={sampleText}
                onChange={e => setSampleText(e.target.value)}
                sx={{flexGrow: 1, minWidth: 320}}
                helperText="샘플 재생성 시 이 텍스트로 음성이 만들어집니다."
            />
            <Button
                variant="outlined"
                size="small"
                onClick={handleSaveSampleText}
                disabled={
                    updateSampleTextMut.isPending ||
                    !sampleText.trim() ||
                    sampleText === initialText
                }
            >
                샘플 텍스트 저장
            </Button>
        </>
    );
};

const AdminTtsVoiceConfigPage: FC = () => {
    const [lang, setLang] = useState<string>('ko');
    const [playingId, setPlayingId] = useState<number | null>(null);
    const [syncResult, setSyncResult] = useState<VoiceSyncResult | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const {data: voices, isLoading} = useTtsVoiceList(lang);
    const syncMut = useSyncTtsVoices();

    // 현재 언어에 저장된 sampleAudioText(여러 voice가 공유) 추출
    const currentSampleText = useMemo(() => {
        const first = voices?.find(v => v.sampleAudioText)?.sampleAudioText;
        return first ?? '';
    }, [voices]);

    const handleSync = () => {
        if (!confirm(`[${lang.toUpperCase()}] Google voices.list로부터 동기화하시겠습니까?\n- 새 voice는 추가되고 기존 voice의 type/gender는 업데이트됩니다.\n- 신규 voice는 샘플 음성도 함께 생성됩니다.`)) return;
        setSyncResult(null);
        syncMut.mutate(
            {langCode: lang},
            {
                onSuccess: (result) => setSyncResult(result),
            },
        );
    };

    const handlePlay = (id: number, url: string) => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        if (playingId === id) {
            setPlayingId(null);
            return;
        }
        const audio = new Audio(url);
        audio.onended = () => setPlayingId(null);
        audioRef.current = audio;
        audio.play()
            .then(() => setPlayingId(id))
            .catch((e) => {
                console.error('Sample play failed', e);
                setPlayingId(null);
            });
    };

    useEffect(() => {
        return () => {
            audioRef.current?.pause();
            audioRef.current = null;
        };
    }, []);

    const rows = voices ?? [];

    return (
        <Box sx={{p: 3}}>
            <Typography variant="h5" gutterBottom>
                TTS Voice 관리
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                언어별 voice 목록을 Google Cloud TTS와 동기화하고, 표시명/기본값/활성 상태를 관리합니다.
            </Typography>

            <Tabs
                value={lang}
                onChange={(_, v) => {
                    audioRef.current?.pause();
                    audioRef.current = null;
                    setPlayingId(null);
                    setLang(v);
                    setSyncResult(null);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{mb: 3, borderBottom: 1, borderColor: 'divider'}}
            >
                {LANGS.map(code => (
                    <Tab key={code} value={code} label={code.toUpperCase()}/>
                ))}
            </Tabs>

            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2, flexWrap: 'wrap', gap: 1}}>
                <SampleTextEditor key={`${lang}:${currentSampleText}`} lang={lang} initialText={currentSampleText}/>
                <Box sx={{flexGrow: 0}}/>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={syncMut.isPending ? <CircularProgress size={16} color="inherit"/> : <CloudDownload/>}
                    onClick={handleSync}
                    disabled={syncMut.isPending}
                >
                    Google 동기화
                </Button>
            </Stack>

            {syncMut.isError && (
                <Alert severity="error" sx={{mb: 2}}>
                    동기화 실패: {(syncMut.error as Error)?.message ?? '알 수 없는 오류'}
                </Alert>
            )}

            {syncResult && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSyncResult(null)}>
                    <Typography variant="body2">
                        [{syncResult.languageCode.toUpperCase()}] 동기화 완료 —
                        총 {syncResult.total}개 · 신규 {syncResult.inserted}개 · 업데이트 {syncResult.updated}개
                        {syncResult.sampleFailures !== undefined && syncResult.sampleFailures > 0 && (
                            <> · 샘플 생성 실패 {syncResult.sampleFailures}개</>
                        )}
                    </Typography>
                    {syncResult.sampleFailureVoices && syncResult.sampleFailureVoices.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            실패 voices: {syncResult.sampleFailureVoices.join(', ')}
                        </Typography>
                    )}
                </Alert>
            )}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Voice Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Gender</TableCell>
                        <TableCell>Display (KO)</TableCell>
                        <TableCell>Display (EN)</TableCell>
                        <TableCell align="center">Default</TableCell>
                        <TableCell align="center">Active</TableCell>
                        <TableCell align="center">Sample</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                <CircularProgress size={24}/>
                            </TableCell>
                        </TableRow>
                    ) : rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                <Typography variant="body2" color="text.secondary">
                                    등록된 voice가 없습니다. "Google 동기화" 버튼으로 가져오세요.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : rows.map(row => {
                        // 서버 값이 변경되면 remount해 VoiceRow 내부 로컬 편집 state를 재동기화
                        const nameSig = `${row.displayNameI18n?.ko ?? ''}|${row.displayNameI18n?.en ?? ''}|${row.isDefault ? 1 : 0}|${row.isActive ? 1 : 0}`;
                        return (
                            <VoiceRow
                                key={`${row.id}:${nameSig}`}
                                row={row}
                                onPlay={handlePlay}
                                playingId={playingId}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default AdminTtsVoiceConfigPage;
